import useStoreSelector from "common/redux/useStoreSelector";
import * as NP from "number-precision";
import { OfferField, OfferFormState, ReducerDefinition } from "./useOfferBuilder";
import { OrderType } from "domain/orderType";
import Offer from "domain/offer";
import usePermission from "common/security/usePermission";
import Permissions from "common/security/permissions";
import { OfferTypeEnum } from "domain/offerType";
import usePositionSettings from "features/positions/usePositionSettings";
import offerTypeHelper from "domain/offerTypeHelper";
import { getSecondLastBusinessDayOfLastMonth, isCashOfferWithOpenStorage } from "./helpers";
import moment from "moment";
import useOfferings from "features/settings/offerings/useOfferings";
import { PositionSetting } from "domain/positionSetting";
import useFcmAccounts from "features/fcmAccounts/useFcmAccounts";
import useHedgingSettings from "features/settings/hedging/useHedgingSettings";
import { listPaymentOptionsForOffer, PaymentOption } from "domain/paymentOption";
import useDeliveryMethods from "features/deliveryMethods/useDeliveryMethods";
import { FeeThin } from "domain/fee";
import { useMemo } from "react";
import { firstBusinessDayOfNextYear, formatTodaysDate } from "common/formatters";
import { TasFallbackOption } from "domain/tasFallbackOption";
import useLocations from "features/locations/useLocations";
import PhysicalLocation, { getPhysicalLocation } from "domain/physicalLocation";
import { DeliveryMethod } from "domain/deliveryMethod";
import calculateNumberOfContracts from "./contractCalculator";
import { roundCents } from "common/decimal";
import { CustomField, CustomFieldThin } from "domain/customField";

export function useAddOfferReducers(roundCashPrice: boolean, listFees: (o: Offer) => FeeThin[],
    listCustomFields: (o: Offer) => CustomField[]): ReducerDefinition[] 
{
    return useOfferReducers(roundCashPrice, listFees, listCustomFields)
    .filter(m => m.excludeForAdds !== true);
}

export function useEditOfferReducers(roundCashPrice: boolean, listFees: (o: Offer) => FeeThin[]): ReducerDefinition[] 
{
    return useOfferReducers(roundCashPrice, listFees, () => [])
        .filter(m => m.excludeForUpdates !== true);
}

function useOfferReducers(roundCashPrice: boolean, listFees: (o: Offer) => FeeThin[], 
    listCustomFields: (o: Offer) => CustomField[]): ReducerDefinition[]
{
    const merchant = useStoreSelector(m => m.userState.activeMerchant);
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const userConfiguration = useStoreSelector(m => m.userState.userConfiguration);
    const positionSettings = usePositionSettings();
    const offerings = useOfferings(true);
    const fcmAccounts = useFcmAccounts();
    const hedgingSettings = useHedgingSettings();
    const deliveryMethods = useDeliveryMethods();
    const locations = useLocations();
    
    const canPlaceHedges = usePermission(Permissions.PlaceLimitOrder);
    const canPushBasis = usePermission(Permissions.PushBasis);
    
    const isMerchantAutoOfferEnabled = merchant.enableAutoOffer;
    const isMerchantAutoPositionEnabled = merchant.enableAutoPosition;

    const reducerDefinitions: ReducerDefinition[] = useMemo(() => [
        {
            //Clear out delivery terms if not allowed
            fields: [OfferField.CustomerWill], 
            reducer: offer => {
                if (offer.deliveryTerms == null) return offer;

                if ((offer.customerWillSell && merchant.displayDeliveryTermsWhenCustomerWillSell) ||
                    (!offer.customerWillSell && merchant.displayDeliveryTermsWhenCustomerWillBuy)) {
                    return offer;
                }

                return {...offer, deliveryTerms: null};
            },
            excludeForUpdates: true
        },
        {
            //Set default delivery terms
            fields: [OfferField.DeliveryMethod, OfferField.Type], 
            reducer: (offer, form, prevOffer) => {
                const old = deliveryMethods.find(dmd => dmd.deliveryMethod === prevOffer.deliveryMethod);
                const replaceDeliveryTerms = offer.deliveryTerms == null || offer.deliveryTerms === old?.defaultDeliveryTerms;
                if (!replaceDeliveryTerms){
                    return offer;
                }

                const newDm = deliveryMethods.find(dmd => dmd.deliveryMethod === offer.deliveryMethod);
                return {
                    ...offer,
                    deliveryTerms: newDm?.defaultDeliveryTerms
                };
            }
        },
        {
            //Reset cash price and price lock
            fields: [OfferField.OrderType], 
            reducer: (offer, form) => {
                form.hasUserUpdatedCashPrice = false;
                
                return  {
                    ...offer,
                    isPriceLocked: false
                };
            }
        },
        {
            //Clear out customer contract ID if not allowed
            fields: [OfferField.CustomerWill], 
            reducer: offer => {
                if (offer.customerContractId == null) return offer;

                if ((offer.customerWillSell && merchant.displayCustomerContractIdForSell) ||
                    (!offer.customerWillSell && merchant.displayCustomerContractIdForBuy)) {
                    return offer;
                }

                return {...offer, customerContractId: null};
            },
            excludeForUpdates: true
        },
        {
            //set buy/sell based on producer default value
            fields: [OfferField.CustomerAccount], 
            reducer: offer => {
                if (offer.producer == null) {
                    return offer;
                }

                const defaultWillSell = offer.producer.willSellDefaultValue
                return {
                    ...offer,
                    customerWillSell: listCustomerWill(offer).includes(defaultWillSell)
                        ? defaultWillSell
                        : !defaultWillSell
                };
            },
            excludeForUpdates: true
        },
        {
            //Determine if the Place Hedge Order toggle should be checked
            fields: [OfferField.CustomerWill, OfferField.OrderType, OfferField.Type], 
            reducer: (offer, form) => {
                form.shouldShowHedgeOrderToggle = shouldShowPlaceHedgeToggle(offer);
                form.placeHedgeOrder = shouldPlaceHedge(offer, form);

                return offer;
            },
            runOnInitialization: true
        },
        {
            //Mark as user having changed Place Hedge Order or clear out hedge info
            fields: [OfferField.PlaceHedge],
            reducer: (offer, form) => {
                const shouldSetDefault = !form.hasUserEnabledHedgeOrder;
                form.hasUserEnabledHedgeOrder = form.placeHedgeOrder;

                if (!form.placeHedgeOrder) {
                    //Clear out hedge settings
                    return {...offer, fcmAccount: null, useMini: null, numberOfContracts: null }
                }

                if (shouldSetDefault) {
                    return getDefaultHedgeSettings(offer);
                }

                return offer;
            },
            runOnInitialization: true
        },
        {
            //Set expiration date
            fields: [OfferField.Type, OfferField.Commodity, OfferField.Symbol, OfferField.DeliveryMethod, OfferField.DeliveryPeriod], 
            reducer: offer => {
                let expirationDate: Date;
                const newOffer = {...offer};
                if (newOffer.commodity?.isCashOnly) {
                    expirationDate = newOffer.deliveryPeriod?.end ?? newOffer.expirationDate;
                }
                else {
                    expirationDate = getExpirationDate(newOffer);
                }

                newOffer.expirationDate = expirationDate;
                return newOffer;
            }
        },
        {
            //Clear invalid payment option
            fields: [OfferField.Type],
            reducer: offer => {
                if (merchant.isPaymentOptionHidden) return offer;

                const allowedPaymentOptions = listPaymentOptionsForOffer(offer.type);

                if (!allowedPaymentOptions.includes(offer.paymentOption)) {
                    return {...offer, paymentOption: allowedPaymentOptions[0], deferredPayDate: null}
                }
            },
            excludeForUpdates: true
        },
        {
            //Set isBasisFixed/HTA Fee
            fields: [OfferField.Type],
            reducer: offer => {
                const result = {...offer}
                if (offerTypeHelper.isHta(offer.type)) {
                    result.isBasisFixed = false;
                    if (!canPushBasis) result.htaFee = result.deliveryPeriod?.htaFee;
                }
                else if (result.type !== OfferTypeEnum.FixBasisOnHta) {
                    result.htaFee = null;
                }

                return result;
            },
            excludeForUpdates: true
        },
        {
            //Set Delivery Period fields
            fields: [OfferField.DeliveryPeriod],
            reducer: offer => {
                const dp = offer.deliveryPeriod;
                return {
                    ...offer,
                    futuresContract: dp?.contract,
                    deliveryPeriodStartDate: dp?.start, 
                    deliveryPeriodEndDate: dp?.end,
                    htaFee: offerTypeHelper.isHta(offer.type) ? dp?.htaFee : offer.htaFee
                };
            }
        },
        {
            //Set default fees
            fields: [OfferField.Type, OfferField.CustomerWill, OfferField.Commodity],
            reducer: offer => {
                return {...offer, fees: listFees(offer).filter(m => m.isDefault) };
            },
            excludeForUpdates: true
        },
        {
            //Set default custom field values
            fields: [OfferField.Type, OfferField.CustomerWill, OfferField.Commodity],
            reducer: offer => {
                return {...offer, customFields: listCustomFields(offer).filter(cf => cf.defaultValue)
                            .map(cf => ({ id: cf.id, name: cf.name, value: cf.defaultValue } as CustomFieldThin))
                };
            },
            excludeForUpdates: true
        },
        {
            //Set Interest start date
            fields: [OfferField.OrderType, OfferField.PaymentOption],
            reducer: offer => {
                const isDeferredPay = offer.paymentOption === PaymentOption.DeferredPay;
                const isLimit = offer.orderType === OrderType.Limit;
                return {
                    ...offer,
                    deferredPayDate: isDeferredPay
                        ? firstBusinessDayOfNextYear()
                        : null,
                    interestRate: isDeferredPay
                        ? merchant.defaultDeferPayInterestRate 
                        : null,
                    interestStartDate: isDeferredPay 
                        ? (!isLimit ? formatTodaysDate('yyyy-MM-DD') : null) 
                        : null,
                    setInterestStartDateOnDateOfFill: isDeferredPay && isLimit 
                        ? true 
                        : null
                }
            }
        },
        {
            //Set TAS Fallback Option
            fields: [OfferField.OrderType],
            reducer: offer => ({
                ...offer,
                tasFallbackOption: offer.orderType === OrderType.Tas && offer?.tasFallbackOption === undefined 
                    ? TasFallbackOption.NotifyOnly 
                    : offer.tasFallbackOption
            })
        },
        {
            //Set region
            fields: [OfferField.Location],
            reducer: offer => ({
                ...offer,
                region: getRegion(offer.location)
            })
        },
        {
            //Set default buy location
            fields: [OfferField.CustomerWill],
            reducer: offer => {
                const defaultBuyLocation = locations.find(l => l.isDefaultBuyLocation);
                if(offer.customerWillSell || defaultBuyLocation == null) return offer;

                return {
                    ...offer,
                    location: defaultBuyLocation, 
                    contractLocation: defaultBuyLocation, 
                    region: getRegion(defaultBuyLocation)
                }
            },
            excludeForUpdates: true
        },
        {
            //Set default delivery method, check order type
            fields: [OfferField.Type],
            reducer: (offer, form) =>  ({ 
                ...offer, 
                orderType: offerTypeHelper.canBeHedged(offer.type) 
                    ? offer.orderType
                    : null, 
                deliveryMethod: !form.lockDeliveryMethod && offer.type === OfferTypeEnum.DelayedPriceContract 
                    ? DeliveryMethod.DelayedPrice 
                    : offer.deliveryMethod 
            }) ,
            excludeForUpdates: true
        },
        {
            //Set number of contracts
            fields: [OfferField.Quantity, OfferField.Commodity, OfferField.PlaceHedge],
            reducer: offer => ({
                ...offer,
                numberOfContracts: calculateNumberOfContracts(
                    offer.commodity, 
                    offer.quantity, 
                    offer.useMini)
            })
        },
        {
            //Set basis
            fields: [OfferField.DeliveryPeriod, OfferField.Location, OfferField.Type],
            reducer: offer => ({
                ...offer,
                basis: getBasis(offer)
            })
        },
        {
            //Calculate cash price
            fields: [OfferField.DeliveryPeriod, OfferField.Symbol, OfferField.Basis, OfferField.OrderType, OfferField.Location],
            reducer: (offer, form) => ({
                    ...offer,
                    cashPrice: getRoundedCashPrice(calculateCashPrice(offer, form), offer, form)
                })
        },
        {
            //Disable price field when moving the cash price one tick
            fields: [OfferField.CashPrice],
            reducer: (offer, form) => {
                if (offer.isPriceLocked) return offer;

                const price = offer.type == OfferTypeEnum.FixFuturesOnBasis ? offer.futuresPrice : offer.cashPrice;
                const step = offer.deliveryPeriod?.commodity.bidPriceIncrement ?? .01;
                const remainder = NP.divide(NP.times(price, 10_000) % NP.times(step, 10_000), 10_000);
                form.allowChangingPrice = remainder !== 0;

                return offer;
            }
        },
        {
            //Cash price rounding
            fields: [OfferField.CashPrice],
            reducer: (offer, form) => ({
                ...offer,
                cashPrice: getRoundedCashPrice(offer.cashPrice, offer, form)
            })
        },
        {
            //Clear out fields for Basis/Delayed Price
            fields: [OfferField.Type],
            reducer: offer => {
                if (![OfferTypeEnum.BasisContract, OfferTypeEnum.DelayedPriceContract].includes(offer.type)) {
                    return offer;
                }

                const newOffer: Offer = {
                    ...offer,
                    cashPrice: null
                };

                if (offer.type === OfferTypeEnum.DelayedPriceContract) {
                    offer.basis = null;
                    offer.futuresContract = null;
                    offer.deliveryPeriod = null;
                    offer.deliveryMethod = merchant.hasErpIntegration ? offer.deliveryMethod : DeliveryMethod.DelayedPrice;
                }

                return newOffer
            },
            excludeForUpdates: true
        }
    ], []);

    function calculateCashPrice(offer: Offer, form: OfferFormState): number {
        if (form.hasUserUpdatedCashPrice || offer.isPriceLocked) return offer.cashPrice;
        
        if (offer.orderType == OrderType.Tas) {
            return 0;
        }

        if (offer.type === OfferTypeEnum.FixBasisOnHta) {
            return NP.plus(offer.futuresPrice ?? 0, offer.basis ?? 0);
        }

        if (offer.commodity.isCashOnly) {
            return offer.deliveryPeriod?.bids?.find(x => x.location.id === offer.location.id).cash;
        }

        return NP.plus(offer.futuresPrice ?? 0, offer.basis ?? 0);
    }

    function shouldRoundCashPrice(offer: Offer) {
        return offerTypeHelper.isCash(offer.type) && offer.deliveryPeriod != null
             && ![OrderType.Tas, OrderType.ManualEntry].includes(offer.orderType);
     }
     
    function getRoundedCashPrice(cashPrice: number, offer: Offer, form: OfferFormState): number {
        if (!roundCashPrice || !shouldRoundCashPrice(offer) || form.hasUserUpdatedCashPrice) {
            return cashPrice;
        }

        return roundCents(cashPrice, offer.futuresContract?.isSettled 
            ? offer.deliveryPeriod.commodity.roundingOnClose
            : offer.deliveryPeriod.commodity.roundingInSession);
    }

    function shouldShowPlaceHedgeToggle(offer: Offer): boolean {
        if(!canPlaceHedges ||
            offer.orderType === OrderType.ManualEntry ||
            [OfferTypeEnum.BasisContract, OfferTypeEnum.DelayedPriceContract, OfferTypeEnum.FixBasisOnHta].includes(offer?.type) ||
            offer.commodity?.isCashOnly) {
            return false;
        }

        const positionSetting = getPositionSetting(offer);

        // Set Futures and Market Order and AO is off and AP is on and side is same as Auto order side
        if(offer.type === OfferTypeEnum.FixFuturesOnBasis && !isMerchantAutoOfferEnabled && isMerchantAutoPositionEnabled && 
            offer.orderType === OrderType.Market && 
            offer.customerWillSell === positionSetting?.autoOrderWillSell)
            return false;

        // AO is off but AP is on
        if(!isMerchantAutoOfferEnabled && isMerchantAutoPositionEnabled)
            return true;

        // HTA Offer - No Location or (AO and AP are both off)
        if (offerTypeHelper.isHta(offer.type) && (offer.location == null || (!isMerchantAutoOfferEnabled && !isMerchantAutoPositionEnabled)))
            return true;

        if (!isMerchantAutoOfferEnabled && 
            (offerTypeHelper.isCash(offer.type) || offer.type === OfferTypeEnum.FixFuturesOnBasis)) {
                return true;
        }

        // AO is on and side is opposite of Auto order side
        // Do check for positionSetting null when not comparing for equality
        if(positionSetting != null && isMerchantAutoPositionEnabled && offer.customerWillSell !== positionSetting.autoOrderWillSell)
            return true;

        return false;
    }

    function shouldPlaceHedge(offer: Offer, form: OfferFormState): boolean {
        if (offer.order != null) {
            return true;
        }

        if (offer.orderType === OrderType.ManualEntry) {
            return true;
        }

        if (!form.shouldShowHedgeOrderToggle) {
            return false;
        }
        
        if (!isMerchantAutoOfferEnabled && 
            offerTypeHelper.isCash(offer.type) && 
            offer.orderType !== OrderType.Tas) {
                return false;
        }
        
        return true;
    }

    function getExpirationDate(offer: Offer): Date {
        if(isCashOfferWithOpenStorage(offer)) {
            return offer.deliveryPeriod?.end;
        }
        
        if(offer.type === OfferTypeEnum.BasisContract) {
            return getSecondLastBusinessDayOfLastMonth(offer.futuresContract?.expiration);
        }

        const offerExpirationDate = moment(offer?.expirationDate);
        const contractExpirationDate = moment(offer.futuresContract?.expiration || null).subtract(1, 'days');
        if (contractExpirationDate.isValid()) {
            return contractExpirationDate.toDate();
        }
        else if (offerExpirationDate.isValid()) {
            return offerExpirationDate.toDate();
        }
        return null;
    }

    function listCustomerWill(forOffer: Offer)
    {
        if (forOffer == null) return [];

        if (!isCustomer)
        {
            if(userConfiguration.canBuy && userConfiguration.canSell)
                return [true, false];
            else 
                return [!userConfiguration.canBuy];
        }  
        
        const offering = offerings
            .find(o => o.location?.id === forOffer.location?.id && o.commodity.id === forOffer.commodity.id);
        
        let customerWillList: boolean[] = [];
        
        if(offering?.allowSelling) customerWillList.push(offering.allowSelling);
        if(offering?.allowBuying) customerWillList.push(!offering.allowBuying);
        
        return customerWillList;
    }

    function getPositionSetting(offer: Offer): PositionSetting {
        return positionSettings.find(x => x.commodity.id === offer.commodity?.id && 
            x.cropYear.id === offer.deliveryPeriod?.cropYear?.id);
    }

    function getDefaultHedgeSettings(offer: Offer): Offer {

        const newOffer = { ...offer};
        let fcmAccountId: number = null;

        const positionSetting = getPositionSetting(offer);
        //AUT-2780 Use L&S FCM account for cash offers
        if (offerTypeHelper.isCash(offer.type) && !isMerchantAutoOfferEnabled && isMerchantAutoPositionEnabled)
        {
            fcmAccountId = positionSetting?.fcmAccount.id;
        }

        if (fcmAccountId == null)
        {
            const offerType = offer.type === OfferTypeEnum.FixFuturesOnBasis ? OfferTypeEnum.Cash : offer.type;
            const hedgingSetting = hedgingSettings?.find(hs => 
                ((offer.location == null && hs.locationId == null) || (offer.location?.id === hs.locationId)) &&
                hs.commodityId === offer.commodity.id && hs.offerTypeId === offerType);
            
            if(hedgingSetting != null)
            {
                fcmAccountId = hedgingSetting.useFcmAccountFromPosition === true ? positionSetting?.fcmAccount.id : hedgingSetting.fcmAccountId;
            }
        }
        
        const defaultFcmAccount = fcmAccounts.find(fa => fa.id === fcmAccountId);
        newOffer.fcmAccount = defaultFcmAccount;

        return newOffer;
    }

    function getRegion(l: PhysicalLocation) {
        if(merchant.displayRegionOnAddOffer) {
            if (l == null) {
                return locations.find(m => m.homeLocation == null);
            }
            else {
                return l.homeLocation != null ? getPhysicalLocation(l.homeLocation) : l;
            }
        } 
        return null;
    }

    function getBasis(offer: Offer): number {
        if (offer.isBasisFixed) return offer.basis;

        if (offer.deliveryPeriod?.commodity?.isCashOnly) {
            return null;
        }
        if (offer.type === OfferTypeEnum.FixFuturesOnBasis) {
            return offer.basis;
        }
        else if (offer.location == null || offerTypeHelper.isHta(offer.type)) {
            return null;
        }

        const bids = offer.deliveryPeriod?.bids?.find(x => x.location?.id === offer.location.id);
        return bids?.basis;
    }

    return reducerDefinitions;
}


